/* eslint-disable no-unused-vars */
import axios from 'axios';
import api from '@src/api';
import _ from 'lodash';
import store from '@state/store';
import localStorage from '@state/localStorage';
const appConfig = require('@src/app.config');

export const state = initialState();

// state is a function so it can be instantiated again - reset
function initialState() {
  return {
    currentUser: localStorage.getSavedState('auth.currentUser'),
  };
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser && state.currentUser.user !== undefined && state.currentUser.user !== null;
  },
  userProfile(state, getters) {
    return getters.loggedIn ? state.currentUser.user.profile : {};
  },
  userProgress(state, getters) {
    return getters.loggedIn ? state.currentUser.user.progress : {};
  },
  userMainGoal(state, getters) {
    return getters.loggedIn ? state.currentUser.user.progress.goals.customGoals[0] : '';
  },
  userMetrics(state, getters) {
    return getters.loggedIn ? state.currentUser.user.metrics : {};
  },
  userProgram(state, getters) {
    return getters.loggedIn ? state.currentUser.user.program : {};
  },
  userOrgId(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isUser) {
        return state.currentUser.user.org_id;
      }
    } else {
      return process.env.VUE_APP_ORG_ID;
    }
  },
  streamAppId(state) {
    if (state.currentUser && state.currentUser.streamAppId) {
      return state.currentUser.streamAppId;
    }
  },
};

export const mutations = {
  setCurrentUser(state, newValue) {
    state.currentUser = _.cloneDeep(newValue);
    localStorage.saveState('auth.currentUser', newValue);
    setDefaultAuthHeaders(state);
  },
  SET_STREAM_APP_ID(state, newValue) {
    if (state.currentUser) {
      state.currentUser.streamAppId = newValue;
    }
    localStorage.saveState('auth.currentUser', state.currentUser);
  },
  reset(state) {
    // acquire initial state
    const s = initialState();
    Object.keys(s).forEach((key) => {
      state[key] = s[key];
    });
  },
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    if (getters.loggedIn) {
      setDefaultAuthHeaders(state);
      dispatch('validate');
    }
  },

  async hydrate({ state, getters, dispatch, commit }) {
    if (
      // Here get ORG data only once and save to current user localstorage
      state &&
      state.currentUser &&
      (!_.has(state.currentUser, 'streamAppId') || !state.currentUser.streamAppId)
    ) {
      await store.dispatch('org/getOrgDetails', getters.userOrgId);
    }
    if (!store.getters['trainer/myTrainer'] || _.isEmpty(store.getters['trainer/myTrainer'])) {
      await store.dispatch('trainer/getMyTrainer', store.getters['client/trainerId']);
    }

    // // // // // // // // // // // // // // // // // // // //
    // HERE ADD ANYTHING YOU WANT TO FIRE ON ROUTER CHANGE   //
    // // // // // // // // // // // // // // // // // // // //
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { username, password } = {}) {
    if (getters.loggedIn) {
      return dispatch('validate');
    }

    const orgId = appConfig.ORG_ID;
    return axios
      .post(api.URL_USER_LOGIN, { email: username, password, org_id: orgId })
      .then((response) => response.data)
      .then((response) => {
        if (response.roles && response.roles.isUser === true) {
          commit('setCurrentUser', response);
          return response;
        } else {
          throw new Error('Username or Password was incorrect.');
        }
      })
      .catch((error) => {
        console.error(error);
        return Promise.reject(error.message);
      });
  },

  resetPassword({ commit, dispatch, getters }, { email } = {}) {
    return axios
      .post(api.URL_RESET_PASSWORD, { org_id: getters.userOrgId, email })
      .then((res) => {
        if (res && res.data) {
          return res.data;
        }
      })
      .catch((error) => {
        console.error(error);
        return error;
      });
  },

  confirmPasswordReset({ commit, dispatch, getters }, { resetType, token, newPass }) {
    const url = api.URL_PASSWORD_RESET_CONFIRM.replace(':modelType', resetType);
    const data = {
      password: newPass,
    };

    return axios
      .post(url, data, { headers: { token: token } })
      .then((response) => {
        const passwordResetConfirmResponse = response.data;
        return passwordResetConfirmResponse;
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  // Logs out the current user.
  logOut({ state, commit }) {
    window.localStorage.removeItem('auth.currentUser');
    window.localStorage.removeItem('onBoardingProgress');
    commit('setCurrentUser', null);

    setDefaultAuthHeaders(state);
  },

  // Client checkout
  checkout({ commit, dispatch, getters }, { price, stripeToken, coupon } = {}) {
    return axios
      .post(api.URL_CLIENT_CHECKOUT, { price, stripeToken, coupon })
      .then((response) => {
        if (response.data.success) {
          dispatch('validate');
          return response.data;
        } else {
          return response.data;
        }
      })
      .catch((error) => {
        console.error(error);
        return error;
      });
  },

  lookupCoupon({ commit, dispatch, getters }, { coupon } = {}) {
    return axios
      .get(api.URL_CLIENT_COUPON_CODE + '?coupon=' + coupon)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state }) {
    if (!state.currentUser) return Promise.resolve(null);

    return axios
      .post(api.URL_VALIDATE_AUTH_TOKEN)
      .then((response) => response.data)
      .then((response) => {
        if (response.user) {
          const user = _.cloneDeep(state.currentUser);
          user.userToken = response.updatedToken;
          user.products = response.products;
          user.user = response.user;
          user.trainerProducts = response.trainerProducts;

          commit('setCurrentUser', user);
          return user;
        } else {
          throw new Error(response.error);
        }
      })
      .catch((err) => {
        if (err.response) {
          console.error(err.response);
        }
        window.localStorage.removeItem('auth.currentUser');
        commit('setCurrentUser', null);
        return null;
      });
  },

  // take an authenticationToken and get's the current user info
  // instead of asking them for username/password.
  autoLogin({ state, commit }, userAccessToken) {
    return axios
      .post(api.URL_VALIDATE_AUTH_TOKEN, '', { headers: { token: userAccessToken } })
      .then((response) => response.data)
      .then((response) => {
        const user = Object.assign(
          {},
          {
            userToken: response.updatedToken,
            products: response.products,
            user: response.user,
            roles: {
              isUser: true,
            },
          }
        );
        commit('setCurrentUser', user);
        return user;
      })
      .catch((err) => {
        if (err.response) {
          console.error(err.response);
        }
        window.localStorage.removeItem('auth.currentUser');
        commit('setCurrentUser', null);
        return null;
      });
  },
};

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.token =
    state.currentUser !== null && state.currentUser !== undefined && state.currentUser.userToken
      ? state.currentUser.userToken
      : '';
}
